import { MotionValue, motion, useSpring, useTransform } from 'framer-motion';
import { useEffect } from 'react';

interface NumberProps {
  mv: MotionValue<number>;
  number: number;
}

function Number({ mv, number }: NumberProps) {
  const y = useTransform(mv, latest => {
    const placeValue = latest % 10;
    const offset = (10 + number - placeValue) % 10;
    let memo = offset * 100;
    if (offset > 5) {
      memo -= 1000;
    }
    return `${memo}%`;
  });

  return (
    <motion.span
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        y
      }}
    >
      {number}
    </motion.span>
  );
}

interface DigitProps {
  place: number;
  value: number;
}

function Digit({ place, value }: DigitProps) {
  const valueRoundedToPlace = Math.floor(value / place);
  const animatedValue = useSpring(valueRoundedToPlace, {
    stiffness: 200,
    damping: 30
  });

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace]);

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '0.6em',
        height: '1em',
        fontVariantNumeric: 'tabular-nums',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 10 }, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} />
      ))}
    </span>
  );
}

interface AnimatedCounterProps {
  value: number;
  className?: string;
}

export default function AnimatedCounter({ value, className = '' }: AnimatedCounterProps) {
  const displayValue = Math.min(99, Math.max(0, value));

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      <Digit place={10} value={displayValue} />
      <Digit place={1} value={displayValue} />
    </span>
  );
}
